import { useCallback, useEffect, useRef, useState } from 'react';
import { userErrorText } from '../lib/userErrorText';
import {
  loadPhoneTimerCalibration,
  micAvailable,
  PhoneTimerSource,
  savePhoneTimerCalibration,
  type AppliedAudioConstraints,
  type PhoneTimerCalibration,
  type PhoneTimerLevel,
} from '../lib/timer/phone';

interface Props {
  onCancel: () => void;
  onDone: (calibration: PhoneTimerCalibration) => void;
}

type Phase =
  | { kind: 'intro' }
  | { kind: 'connecting' }
  | { kind: 'permission-denied'; message: string }
  | { kind: 'ambient'; remainingMs: number }
  | { kind: 'roundtrip'; sampleNum: number }
  | { kind: 'shot' }
  | { kind: 'review' };

const AMBIENT_MS = 2000;
const ROUNDTRIP_SAMPLES = 5;
const ROUNDTRIP_TIMEOUT_MS = 1500;

/**
 * Two-phase calibration wizard for the phone-mic shot timer.
 *
 *   1. Intro / permission — clears the mic-input pipe, surfaces the
 *      `getUserMedia` constraints that did/didn't apply (Safari ignores
 *      some), and warns on iOS phone-mode risk.
 *   2. Ambient (2 s)        — collects RMS distribution of the room;
 *                             noiseFloor = mean + 3·stdev.
 *   3. Round-trip (5 takes) — plays a 1 kHz / 50 ms tone, waits for the
 *                             peak to come back through the mic, takes
 *                             the median delta as audioRoundTripMs.
 *   4. Test shot            — shooter fires once; aAbs = 30 % of peak
 *                             RMS observed during a 4 s window.
 *   5. Review               — shows the captured numbers; saving them
 *                             persists to localStorage and returns to
 *                             the timing pad.
 *
 * Calibration is per-device; results live in localStorage under
 * `phoneTimer.calibration.v1`.
 */
export function PhoneTimerCalibrationScreen({ onCancel, onDone }: Props) {
  const sourceRef = useRef<PhoneTimerSource | null>(null);
  const ambientSamplesRef = useRef<number[]>([]);
  const roundtripSamplesRef = useRef<number[]>([]);
  const shotPeakRef = useRef<number>(0);
  /** True while the test-shot window is open; the level listener max-holds into shotPeakRef. */
  const shotWatchRef = useRef(false);
  const tonePlayedAtRef = useRef<number | null>(null);
  const lastLevelRef = useRef<PhoneTimerLevel | null>(null);

  const [phase, setPhase] = useState<Phase>({ kind: 'intro' });
  const [applied, setApplied] = useState<AppliedAudioConstraints | null>(null);
  const [level, setLevel] = useState<PhoneTimerLevel | null>(null);
  const [result, setResult] = useState<{
    noiseFloor: number;
    aAbs: number;
    audioRoundTripMs: number;
  } | null>(null);

  const cleanup = useCallback(async () => {
    const src = sourceRef.current;
    sourceRef.current = null;
    if (src) {
      try {
        await src.disconnect();
      } catch {
        // No-op — we're tearing down anyway.
      }
    }
  }, []);

  useEffect(() => {
    return () => {
      void cleanup();
    };
  }, [cleanup]);

  // Real-time level reports drive the meter and per-phase analysis.
  useEffect(() => {
    const src = sourceRef.current;
    if (!src) return;
    return src.onLevel((lv) => {
      lastLevelRef.current = lv;
      // Every report (not a 50 ms poll of the latest one), using the
      // report's max-hold peak — a gunshot impulse lasts only a few ms.
      if (shotWatchRef.current && lv.peakRms > shotPeakRef.current) {
        shotPeakRef.current = lv.peakRms;
      }
      setLevel(lv);
    });
  }, [phase.kind]);

  const startConnect = useCallback(async () => {
    if (!micAvailable()) {
      setPhase({
        kind: 'permission-denied',
        message: 'Mikrofon-API in diesem Browser nicht verfügbar.',
      });
      return;
    }
    setPhase({ kind: 'connecting' });
    const src = new PhoneTimerSource();
    sourceRef.current = src;
    try {
      const r = await src.connect();
      setApplied(r.applied);
      ambientSamplesRef.current = [];
      const start = performance.now();
      setPhase({ kind: 'ambient', remainingMs: AMBIENT_MS });
      const tick = () => {
        const elapsed = performance.now() - start;
        const remaining = Math.max(0, AMBIENT_MS - elapsed);
        if (sourceRef.current !== src) return; // unmounted / cancelled
        if (lastLevelRef.current) ambientSamplesRef.current.push(lastLevelRef.current.rms);
        if (remaining <= 0) {
          finishAmbient();
          return;
        }
        setPhase({ kind: 'ambient', remainingMs: remaining });
        setTimeout(tick, 50);
      };
      setTimeout(tick, 50);
    } catch (err) {
      const message = userErrorText(err instanceof Error ? err : 'mic_connect_failed', 'mic');
      setPhase({ kind: 'permission-denied', message });
      await cleanup();
    }
  }, [cleanup]);

  function finishAmbient() {
    const samples = ambientSamplesRef.current;
    if (samples.length < 4) {
      setPhase({
        kind: 'permission-denied',
        message: 'Zu wenige Pegel-Messwerte. Bitte erneut versuchen.',
      });
      return;
    }
    setPhase({ kind: 'roundtrip', sampleNum: 1 });
    runRoundtripTake(1);
  }

  function runRoundtripTake(takeNum: number) {
    const src = sourceRef.current;
    if (!src) return;
    roundtripSamplesRef.current = roundtripSamplesRef.current.slice(0, takeNum - 1);
    setTimeout(() => {
      if (sourceRef.current !== src) return;
      const sched = src.playCalibrationTone({ durMs: 50, freqHz: 1000, gain: 0.6 });
      if (!sched) return;
      tonePlayedAtRef.current = sched.scheduledAtCtxTime;
      const before = lastLevelRef.current?.rms ?? 0;
      const baselineRms = Math.max(before, 0.001);
      const triggerRms = baselineRms * 4;
      const startWall = performance.now();
      let captured = false;

      const poll = () => {
        if (sourceRef.current !== src || captured) return;
        const wallElapsed = performance.now() - startWall;
        const lv = lastLevelRef.current;
        if (
          lv &&
          tonePlayedAtRef.current != null &&
          lv.peakT > tonePlayedAtRef.current &&
          lv.peakRms > triggerRms
        ) {
          // peakT = onset of the loudest frame in the report window, at
          // render-quantum (≈2.7 ms) resolution instead of the ~32 ms
          // report spacing.
          const latencyMs = (lv.peakT - tonePlayedAtRef.current) * 1000;
          // Sane-bound check: ignore obviously bogus samples.
          if (latencyMs >= 0 && latencyMs <= 500) {
            roundtripSamplesRef.current.push(latencyMs);
            captured = true;
            advanceRoundtrip(takeNum);
            return;
          }
        }
        if (wallElapsed > ROUNDTRIP_TIMEOUT_MS) {
          // Tone was inaudible to the mic — record a sentinel and move
          // on. If all takes fail the wizard finalizes at 0 ms.
          captured = true;
          advanceRoundtrip(takeNum);
          return;
        }
        setTimeout(poll, 20);
      };
      setTimeout(poll, 20);
    }, 250);
  }

  function advanceRoundtrip(takeNum: number) {
    if (takeNum >= ROUNDTRIP_SAMPLES) {
      finishRoundtrip();
      return;
    }
    const next = takeNum + 1;
    setPhase({ kind: 'roundtrip', sampleNum: next });
    runRoundtripTake(next);
  }

  function finishRoundtrip() {
    setPhase({ kind: 'shot' });
    shotPeakRef.current = 0;
    shotWatchRef.current = true;
    const start = performance.now();
    const watchdog = setInterval(() => {
      if (performance.now() - start > 8000) {
        shotWatchRef.current = false;
        clearInterval(watchdog);
      }
    }, 50);
    // The user clicks "Schuss erfasst" or "Überspringen" to advance.
    (window as unknown as { __phoneCalShotWatch: ReturnType<typeof setInterval> })
      .__phoneCalShotWatch = watchdog;
  }

  function captureShot() {
    shotWatchRef.current = false;
    const watch = (window as unknown as {
      __phoneCalShotWatch?: ReturnType<typeof setInterval>;
    }).__phoneCalShotWatch;
    if (watch) clearInterval(watch);
    finalizeReview(shotPeakRef.current);
  }

  function skipShot() {
    shotWatchRef.current = false;
    const watch = (window as unknown as {
      __phoneCalShotWatch?: ReturnType<typeof setInterval>;
    }).__phoneCalShotWatch;
    if (watch) clearInterval(watch);
    finalizeReview(0);
  }

  function finalizeReview(shotPeak: number) {
    const ambient = ambientSamplesRef.current;
    const mean = ambient.reduce((a, b) => a + b, 0) / ambient.length;
    const variance =
      ambient.reduce((a, b) => a + (b - mean) ** 2, 0) / Math.max(1, ambient.length - 1);
    const stdev = Math.sqrt(variance);
    const noiseFloor = mean + 3 * stdev;

    const aAbs = shotPeak > 0 ? Math.max(noiseFloor * 8, shotPeak * 0.3) : Math.max(0.05, noiseFloor * 8);

    const samples = roundtripSamplesRef.current.slice().sort((a, b) => a - b);
    const audioRoundTripMs = samples.length > 0 ? samples[Math.floor(samples.length / 2)] ?? 0 : 0;

    setResult({ noiseFloor, aAbs, audioRoundTripMs });
    setPhase({ kind: 'review' });
  }

  async function save() {
    if (!result) return;
    const cal: PhoneTimerCalibration = {
      noiseFloor: result.noiseFloor,
      aAbs: result.aAbs,
      audioRoundTripMs: result.audioRoundTripMs,
      capturedAt: Date.now(),
    };
    savePhoneTimerCalibration(cal);
    await cleanup();
    onDone(cal);
  }

  async function cancel() {
    await cleanup();
    onCancel();
  }

  // ---------- rendering --------------------------------------------------

  return (
    <section className="space-y-4">
      <header className="flex items-center justify-between">
        <h2 className="text-xl font-bold">Phone-Timer kalibrieren</h2>
        <button
          onClick={() => void cancel()}
          className="rounded border border-zinc-600 px-3 py-1 text-sm text-zinc-300 hover:border-zinc-400"
        >
          Abbrechen
        </button>
      </header>

      <div className="rounded-lg border border-amber-700/40 bg-amber-950/30 p-3 text-xs text-amber-200">
        <strong>Beta — nur fürs Solo-Training.</strong> Die Erkennung über das Telefon­mikrofon
        ist nicht turnier­tauglich. Andere Schützen auf benachbarten Ständen lösen Fehl­treffer
        aus.
      </div>

      {phase.kind === 'intro' && (
        <Card title="1 · Kalibrierung starten">
          <p className="text-sm text-zinc-300">
            Lege das Telefon dorthin, wo es während des Trainings liegen wird. Wir messen das
            Hintergrund­geräusch und die Speaker→Mikrofon-Latenz. Anschließend feuerst Du einen
            Test­schuss zum Pegeln des Schwell­werts.
          </p>
          <p className="mt-2 text-xs text-zinc-500">
            Beim ersten Tap fragt der Browser nach Mikrofon-Zugriff.
          </p>
          <button
            onClick={() => void startConnect()}
            className="mt-3 w-full rounded-lg bg-emerald-700 px-4 py-3 text-sm font-bold text-white hover:bg-emerald-600"
          >
            Mikrofon freigeben & starten →
          </button>
        </Card>
      )}

      {phase.kind === 'connecting' && (
        <Card title="Mikrofon wird angefordert…">
          <p className="text-sm text-zinc-400">Bitte den Browser-Dialog bestätigen.</p>
        </Card>
      )}

      {phase.kind === 'permission-denied' && (
        <Card title="Fehler">
          <p className="text-sm text-red-300">{phase.message}</p>
          <button
            onClick={() => setPhase({ kind: 'intro' })}
            className="mt-3 rounded border border-zinc-600 px-3 py-2 text-sm text-zinc-300 hover:border-zinc-400"
          >
            ↺ Erneut versuchen
          </button>
        </Card>
      )}

      {phase.kind === 'ambient' && (
        <Card title="2 · Hintergrund-Geräusch …">
          <p className="text-sm text-zinc-300">
            Bitte einen Moment ruhig sein, das Telefon liegt am Schießstand-Platz.
          </p>
          <Progress value={1 - phase.remainingMs / AMBIENT_MS} />
          <LevelMeter level={level} />
          <ConstraintBadge applied={applied} />
        </Card>
      )}

      {phase.kind === 'roundtrip' && (
        <Card title={`3 · Audio-Latenz (Take ${phase.sampleNum} / ${ROUNDTRIP_SAMPLES})`}>
          <p className="text-sm text-zinc-300">
            Ein 1-kHz-Ton wird kurz abgespielt; das Mikrofon hört ihn. So messen wir den
            Hin-und-Rück-Weg der Audio-Pipeline.
          </p>
          <Progress value={phase.sampleNum / ROUNDTRIP_SAMPLES} />
          <LevelMeter level={level} />
          {roundtripSamplesRef.current.length > 0 && (
            <p className="mt-2 font-mono text-xs text-zinc-500">
              Ergebnisse: {roundtripSamplesRef.current.map((m) => `${m.toFixed(0)}ms`).join(' · ')}
            </p>
          )}
        </Card>
      )}

      {phase.kind === 'shot' && (
        <Card title="4 · Test­schuss">
          <p className="text-sm text-zinc-300">
            Einen Schuss abgeben (oder eine sehr laute Klatsch-Bewegung in 1 m Abstand). Tippe danach
            unten auf <strong>„Schuss erfasst"</strong>.
          </p>
          <LevelMeter level={level} peak={shotPeakRef.current} />
          <p className="mt-2 font-mono text-xs text-zinc-500">
            Spitzenpegel: {(shotPeakRef.current * 100).toFixed(1)} %
          </p>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <button
              onClick={captureShot}
              disabled={shotPeakRef.current < 0.02}
              className="rounded-lg bg-emerald-700 px-3 py-3 text-sm font-bold text-white hover:bg-emerald-600 disabled:opacity-30"
            >
              ✓ Schuss erfasst
            </button>
            <button
              onClick={skipShot}
              className="rounded-lg border border-zinc-600 px-3 py-3 text-sm text-zinc-300 hover:border-zinc-400"
            >
              Überspringen (Default-Schwelle)
            </button>
          </div>
        </Card>
      )}

      {phase.kind === 'review' && result && (
        <Card title="5 · Übersicht">
          <dl className="space-y-1 font-mono text-xs text-zinc-300">
            <Row k="Geräusch-Untergrund" v={`${(result.noiseFloor * 100).toFixed(2)} %`} />
            <Row k="Trigger-Schwelle (a_abs)" v={`${(result.aAbs * 100).toFixed(2)} %`} />
            <Row k="Audio-Latenz (Median)" v={`${result.audioRoundTripMs.toFixed(0)} ms`} />
          </dl>
          <ConstraintBadge applied={applied} />
          <div className="mt-3 grid grid-cols-2 gap-2">
            <button
              onClick={() => void save()}
              className="rounded-lg bg-emerald-700 px-3 py-3 text-sm font-bold text-white hover:bg-emerald-600"
            >
              ✓ Übernehmen
            </button>
            <button
              onClick={() => void cancel()}
              className="rounded-lg border border-zinc-600 px-3 py-3 text-sm text-zinc-300 hover:border-zinc-400"
            >
              Verwerfen
            </button>
          </div>
        </Card>
      )}

      <PreviousCalibrationHint />
    </section>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-4">
      <h3 className="mb-2 text-sm font-bold text-zinc-300">{title}</h3>
      {children}
    </div>
  );
}

function Progress({ value }: { value: number }) {
  const pct = Math.max(0, Math.min(1, value)) * 100;
  return (
    <div className="mt-3 h-2 overflow-hidden rounded-full bg-zinc-800">
      <div
        className="h-full bg-emerald-600 transition-[width]"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

function LevelMeter({ level, peak }: { level: PhoneTimerLevel | null; peak?: number }) {
  if (!level) return null;
  const rmsPct = Math.min(100, level.rms * 100 * 4);
  const noisePct = Math.min(100, level.noiseFloor * 100 * 4);
  const peakPct = peak != null ? Math.min(100, peak * 100 * 4) : null;
  return (
    <div className="mt-3 h-3 overflow-hidden rounded-full bg-zinc-800">
      <div className="relative h-full">
        <div
          className="absolute h-full bg-zinc-600"
          style={{ width: `${noisePct}%` }}
          title={`noise floor ${(level.noiseFloor * 100).toFixed(2)}%`}
        />
        <div
          className="absolute h-full bg-emerald-500"
          style={{ width: `${rmsPct}%` }}
          title={`rms ${(level.rms * 100).toFixed(2)}%`}
        />
        {peakPct != null && peakPct > 0 && (
          <div
            className="absolute h-full w-0.5 bg-amber-300"
            style={{ left: `${peakPct}%` }}
            title={`peak ${(peak! * 100).toFixed(2)}%`}
          />
        )}
      </div>
    </div>
  );
}

function ConstraintBadge({ applied }: { applied: AppliedAudioConstraints | null }) {
  if (!applied) return null;
  const ok =
    !applied.echoCancellation && !applied.autoGainControl && !applied.noiseSuppression;
  return (
    <p
      className={`mt-3 rounded px-2 py-1 text-xs ${
        ok ? 'bg-emerald-900/30 text-emerald-200' : 'bg-amber-900/30 text-amber-200'
      }`}
    >
      {ok
        ? 'Audio-Verarbeitung deaktiviert (AGC / Echo / Noise off) — gut.'
        : `Browser hat ${[
            applied.echoCancellation ? 'echo' : '',
            applied.autoGainControl ? 'AGC' : '',
            applied.noiseSuppression ? 'noise' : '',
          ]
            .filter(Boolean)
            .join('+')} aktiv gelassen — Genauigkeit reduziert.`}
    </p>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between border-b border-zinc-800/40 py-1 last:border-0">
      <span className="text-zinc-500">{k}</span>
      <span>{v}</span>
    </div>
  );
}

function PreviousCalibrationHint() {
  const [prev, setPrev] = useState<PhoneTimerCalibration | null>(null);
  useEffect(() => {
    setPrev(loadPhoneTimerCalibration());
  }, []);
  if (!prev) return null;
  const ageH = Math.round((Date.now() - prev.capturedAt) / 36e5);
  return (
    <p className="text-xs text-zinc-500">
      Letzte gespeicherte Kalibrierung: vor {ageH} h · noiseFloor=
      {(prev.noiseFloor * 100).toFixed(2)}% · aAbs={(prev.aAbs * 100).toFixed(2)}% · rt=
      {prev.audioRoundTripMs.toFixed(0)}ms
    </p>
  );
}
